// identifierNames.js
// The one rule for a name that becomes a storage identifier. A table's name is
// its Firestore document id and its BigQuery table id; a connection's name is
// its Firestore document id and part of its Secret Manager secret id.
//
// This validates the name AS TYPED, with spaces. The name is posted as typed and
// the BACKEND turns the spaces into underscores (sanitize_column_name) to derive
// the id — normalising here first would post an underscore form that the backend's
// own no-underscore check then rejects. After the substitution the id must be
// letters, digits, underscores or dashes — the intersection of what all three
// stores accept — and must not be Firestore-reserved ("__x__"). A typed underscore
// is refused outright: "Claims 2026" and "Claims_2026" would otherwise become the
// same id.
//
// A table's name has one rule more (tableNameError): two spaces in a row are
// refused, since they become "__", and a table's companion assertions table is
// named "{table}__assertions", so "Premiums  assertions" would be another table's
// assertions table. A connection has no companion table.
//
// Mirrors the backend (create_table.py): identifierNameError is
// validate_identifier_name, and tableNameError is validate_table_schema's checks
// of the table name. Same checks, same wording, so the field-level message is the
// same whichever side catches it.

export const IDENTIFIER_NAME_MAX = 255;

export function identifierNameError(name, noun = 'Name') {
  const trimmed = String(name ?? '').trim();
  if (!trimmed) return `${noun} is required`;
  if (trimmed.includes('_')) return `${noun} cannot contain "_" — use a space instead`;
  const id = trimmed.replace(/ /g, '_');
  if (!/^[A-Za-z0-9_-]+$/.test(id)) {
    return `${noun} can only contain letters, numbers, spaces and dashes`;
  }
  if (!/[A-Za-z0-9]/.test(id)) return `${noun} must contain at least one letter or number`;
  if (id.startsWith('__') && id.endsWith('__')) return `${noun} cannot start and end with double underscores`;
  if (id.length > IDENTIFIER_NAME_MAX) return `${noun} is too long (max ${IDENTIFIER_NAME_MAX} characters)`;
  return null;
}

export function tableNameError(name) {
  const identifierError = identifierNameError(name, 'Table name');
  if (identifierError) return identifierError;
  const id = String(name).trim().replace(/ /g, '_');
  if (id.includes('__')) return 'Table name cannot contain two spaces in a row';
  return null;
}
